import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatRM, formatDate, STATUSES, statusColor } from "@/lib/api";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { StatusBadge, AgentBadge } from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import {
  Sparkles, Clock, AlertTriangle, CalendarClock, PenSquare,
  Wallet, Package, Users2, Layers, CheckCircle2
} from "lucide-react";

const StatCard = ({ icon: Icon, label, value, accent, testid, onClick }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`text-left p-5 rounded-xl border bg-card hover:shadow-md transition-all ${accent}`}
  >
    <div className="flex items-center justify-between mb-3">
      <Icon className="w-5 h-5 text-muted-foreground" />
    </div>
    <div className="text-3xl font-display font-bold tracking-tight">{value}</div>
    <div className="text-sm text-muted-foreground mt-1">{label}</div>
  </button>
);

const AlertList = ({ icon: Icon, title, items, accent, testid, nav, render }) => (
  <Card className={`p-4 border-l-4 ${accent}`} data-testid={testid}>
    <div className="flex items-center justify-between mb-2">
      <div className="flex items-center gap-2 font-display font-bold text-sm"><Icon className="w-4 h-4" /> {title}</div>
      <Badge variant="secondary" className="font-mono">{items.length}</Badge>
    </div>
    {items.length === 0 && <div className="text-xs text-muted-foreground">None — all clear</div>}
    <div className="divide-y divide-border">
      {items.map((j) => (
        <button key={j.id} onClick={() => nav(`/jobs/${j.id}`)} className="w-full text-left py-1.5 flex justify-between gap-2 hover:bg-muted/50 rounded px-1 text-xs" data-testid={`alert-job-${j.job_number}`}>
          <span><span className="font-mono font-semibold">{j.job_number}</span> <span className="text-muted-foreground">{j.customer_name}</span></span>
          <span className="font-mono shrink-0">{render(j)}</span>
        </button>
      ))}
    </div>
  </Card>
);

const AlertsPanel = ({ alerts, nav, showSales }) => {
  if (!alerts) return null;
  return (
    <div className={`grid grid-cols-1 ${showSales ? "md:grid-cols-3" : "md:grid-cols-2"} gap-4`} data-testid="alerts-panel">
      <AlertList icon={Clock} title="Overdue Deadlines" items={alerts.overdue} accent="border-l-rose-500" testid="alert-overdue" nav={nav} render={(j) => formatDate(j.deadline)} />
      <AlertList icon={PenSquare} title="Artwork Awaiting Approval" items={alerts.artwork_pending} accent="border-l-fuchsia-500" testid="alert-artwork" nav={nav} render={(j) => j.status} />
      {showSales && <AlertList icon={Wallet} title="Outstanding Payments" items={alerts.payment_pending} accent="border-l-amber-500" testid="alert-payment" nav={nav} render={(j) => formatRM(j.outstanding)} />}
    </div>
  );
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const nav = useNavigate();
  const { hasPerm } = useAuth();

  const load = () => api.get("/dashboard/stats").then((r) => setStats(r.data));
  useEffect(() => { load(); }, []);
  useAutoRefresh(load, 20000);

  if (!stats) return <div className="text-muted-foreground">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <div className="text-xs font-mono text-muted-foreground uppercase tracking-wider">Operations Command Center</div>
        <h1 className="text-3xl sm:text-4xl font-display font-extrabold tracking-tight mt-1">Welcome back to PrintOps</h1>
        <p className="text-muted-foreground mt-1">Today's operations summary{hasPerm("sales") && <span data-testid="dashboard-revenue"> • {formatRM(stats.total_revenue)} collected to date</span>}</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Sparkles} label="New Job" value={stats.new_jobs} testid="stat-new-jobs" onClick={() => nav("/jobs?status=To%20Confirm")} />
        <StatCard icon={CalendarClock} label="Due Today" value={stats.due_today} testid="stat-due-today" onClick={() => nav("/jobs?due=today")} />
        <StatCard icon={AlertTriangle} label="Urgent" value={stats.urgent} testid="stat-urgent" onClick={() => nav("/jobs?priority=urgent")} accent="border-amber-300" />
        <StatCard icon={Clock} label="Overdue" value={stats.overdue} testid="stat-overdue" onClick={() => nav("/jobs?due=overdue")} accent="border-rose-300" />
        <StatCard icon={PenSquare} label="Artwork Pending" value={stats.artwork_pending} testid="stat-artwork" onClick={() => nav("/jobs?status=Reviewing")} />
        <StatCard icon={Package} label="Ready for Pickup" value={stats.ready_pickup} testid="stat-pickup" onClick={() => nav("/jobs?status=Ready%20for%20Pick-Up")} />
        <StatCard icon={CheckCircle2} label="Collected" value={stats.collected} testid="stat-collected" onClick={() => nav("/jobs?status=Collected")} accent="border-purple-300" />
        <StatCard icon={Users2} label="Customers" value={stats.total_customers} testid="stat-customers" onClick={() => nav("/customers")} />
      </div>

      <AlertsPanel alerts={stats.alerts} nav={nav} showSales={hasPerm("sales")} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-bold text-lg">Recent Jobs</h2>
            <button onClick={() => nav("/jobs")} className="text-sm text-cyan-600 hover:underline" data-testid="view-all-jobs">View all →</button>
          </div>
          <div className="divide-y divide-border">
            {stats.recent_jobs.length === 0 && <div className="py-6 text-sm text-muted-foreground text-center">No jobs yet. Click "New Order" to start.</div>}
            {stats.recent_jobs.map((j) => (
              <button
                key={j.id}
                onClick={() => nav(`/jobs/${j.id}`)}
                className="w-full py-3 flex items-center justify-between hover:bg-muted/50 px-2 rounded transition text-left"
                style={{ borderLeft: `4px solid ${statusColor(j.status)}` }}
                data-testid={`recent-job-${j.job_number}`}
              >
                <div>
                  <div className="font-mono text-sm font-semibold flex items-center gap-2">{j.job_number}<AgentBadge job={j} /></div>
                  <div className="text-sm text-muted-foreground">{j.customer_name} • {j.product}</div>
                </div>
                <div className="flex items-center gap-3">
                  {hasPerm("sales") && <span className="font-mono text-sm">{formatRM(j.total_price)}</span>}
                  <StatusBadge status={j.status} />
                </div>
              </button>
            ))}
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <Layers className="w-4 h-4" />
            <h2 className="font-display font-bold text-lg">Status Breakdown</h2>
          </div>
          <div className="space-y-2">
            {STATUSES.map((s) => {
              const n = stats.status_counts[s] || 0; const pct = stats.total_jobs ? Math.round((n / stats.total_jobs) * 100) : 0;
              return (
                <button key={s} onClick={() => nav(`/jobs?status=${encodeURIComponent(s)}`)} className="w-full text-left space-y-1 hover:bg-muted/50 rounded px-1 py-1" data-testid={`status-row-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <div className="flex items-center justify-between text-sm"><StatusBadge status={s} /><Badge variant="secondary" className="font-mono">{n}</Badge></div>
                  <div className="h-1.5 rounded-full bg-muted overflow-hidden"><div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: statusColor(s), border: statusColor(s) === "#ffffff" ? "1px solid #94a3b8" : "none" }} /></div>
                </button>
              );
            })}
          </div>
        </Card>
      </div>
    </div>
  );
}
